import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  ViewerSettings,
  LANGUAGE_OPTIONS,
  TEXT_SIZE_OPTIONS,
} from "../../types/settings";
import { t } from "../../utils/i18n";

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  settings: ViewerSettings;
  language?: ViewerSettings["language"];
  themeMode?: ViewerSettings["theme"];
  onSettingsChange: (settings: ViewerSettings) => void;
  onReplayTutorial: () => void;
  onResetViewer: () => void;
  title?: string;
  titleIcon?: string;
  backgroundColor?: string;
  backdropBlur?: string;
  borderColor?: string;
  boxShadow?: string;
  primaryTextColor?: string;
  secondaryTextColor?: string;
  accentColor?: string;
  fontFamily?: string;
}

export function SettingsModal({
  isOpen,
  onClose,
  settings,
  language,
  themeMode = "dark",
  onSettingsChange,
  onReplayTutorial,
  onResetViewer,
  title = "Settings",
  titleIcon = "settings",
  backgroundColor = "rgba(255, 255, 255, 0.04)",
  backdropBlur = "40px",
  borderColor = "rgba(255, 255, 255, 0.1)",
  boxShadow = "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
  primaryTextColor = "rgba(255, 255, 255, 0.95)",
  secondaryTextColor = "rgba(255, 255, 255, 0.7)",
  accentColor = "rgb(56, 189, 248)",
  fontFamily = "Arial, sans-serif",
}: SettingsModalProps) {
  const activeLanguage = language ?? settings.language;
  const translatedTitle =
    title === "Settings" ? t(activeLanguage, "settings.title") : title;
  const languageDisplayNames = useMemo(() => {
    const locale = activeLanguage === "zh" ? "zh-Hans" : activeLanguage;
    try {
      return new Intl.DisplayNames([locale], { type: "language" });
    } catch {
      return null;
    }
  }, [activeLanguage]);

  const getLanguageOptionLabel = (value: ViewerSettings["language"]) => {
    if (languageDisplayNames) {
      const localizedLabel = languageDisplayNames.of(value);
      if (localizedLabel) {
        return localizedLabel;
      }
    }

    switch (value) {
      case "es":
        return "Espa\u00f1ol";
      case "fr":
        return "Fran\u00e7ais";
      case "de":
        return "Deutsch";
      case "zh":
        return "\u4e2d\u6587";
      case "en":
      default:
        return "English";
    }
  };

  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const isLightMode = themeMode === "light";
  const sectionBackground = isLightMode
    ? "rgba(15, 23, 42, 0.06)"
    : "rgba(255, 255, 255, 0.02)";
  const sectionHoverBackground = isLightMode
    ? "rgba(15, 23, 42, 0.1)"
    : "rgba(255, 255, 255, 0.06)";
  const hoverBorderColor = isLightMode
    ? "rgba(15, 23, 42, 0.24)"
    : "rgba(255, 255, 255, 0.2)";
  const selectBackground = isLightMode
    ? "rgba(15, 23, 42, 0.06)"
    : "rgba(255, 255, 255, 0.04)";
  const selectOptionBackground = isLightMode ? "#f8fafc" : "#1a1c23";
  const selectOptionText = isLightMode
    ? "rgba(15, 23, 42, 0.92)"
    : "rgba(255, 255, 255, 0.95)";

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      const openTimer = window.setTimeout(() => setIsVisible(true), 10);
      return () => window.clearTimeout(openTimer);
    }

    setIsVisible(false);
    const closeTimer = window.setTimeout(() => setShouldRender(false), 300);
    return () => window.clearTimeout(closeTimer);
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [isOpen, onClose]);

  const handleThemeToggle = () => {
    onSettingsChange({
      ...settings,
      theme: settings.theme === "dark" ? "light" : "dark",
    });
  };

  const handleLanguageChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    onSettingsChange({
      ...settings,
      language: event.target.value as ViewerSettings["language"],
    });
  };

  const handleTextSizeChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    onSettingsChange({
      ...settings,
      textSize: event.target.value as ViewerSettings["textSize"],
    });
  };

  if (!shouldRender) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 250,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: isLightMode
          ? "rgba(255, 255, 255, 0.4)"
          : "rgba(15, 17, 21, 0.6)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        opacity: isVisible ? 1 : 0,
        transition: "opacity 0.25s ease-in-out",
        fontFamily,
        padding: "1.5rem",
      }}
      onClick={onClose}
      role="presentation"
      data-oid="kmkzk5l"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-modal-title"
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "min(520px, 90vw)",
          maxHeight: "85vh",
          borderRadius: "2rem",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          backgroundColor,
          backdropFilter: `blur(${backdropBlur})`,
          WebkitBackdropFilter: `blur(${backdropBlur})`,
          border: `1px solid ${borderColor}`,
          boxShadow,
          transform: isVisible ? "scale(1)" : "scale(0.95)",
          transition: "transform 0.25s ease-in-out",
        }}
        data-oid="q06kag5"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          aria-label={t(activeLanguage, "settings.close")}
          style={{
            position: "absolute",
            top: "1.5rem",
            right: "1.5rem",
            width: "2.5rem",
            height: "2.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "9999px",
            background: "transparent",
            border: "none",
            color: secondaryTextColor,
            cursor: "pointer",
            transition: "all 0.3s",
            zIndex: 10,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
            e.currentTarget.style.color = primaryTextColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = secondaryTextColor;
          }}
          data-oid="u.rgerc"
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: "1.25rem", fontWeight: 300 }}
            data-oid="dekcob:"
          >
            close
          </span>
        </button>

        {/* Header */}
        <div style={{ padding: "1.5rem 2rem 1rem 2rem" }} data-oid="gszted:">
          <div
            style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            data-oid="j1e25eq"
          >
            <span
              className="material-symbols-outlined"
              style={{
                color: accentColor,
                fontSize: "1.25rem",
                fontWeight: 300,
              }}
              data-oid="f9xeuj1"
            >
              {titleIcon}
            </span>
            <h2
              id="settings-modal-title"
              style={{
                margin: 0,
                fontSize: "1.1rem",
                fontWeight: 700,
                letterSpacing: "0.025em",
                color: primaryTextColor,
              }}
              data-oid="44ppiv1"
            >
              {translatedTitle}
            </h2>
          </div>
        </div>

        {/* Content */}
        <div
          style={{
            padding: "0 2rem 1.5rem 2rem",
            overflowY: "auto",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "1.5rem",
          }}
          data-oid="_7g8-2r"
        >
          {/* Appearance Section */}
          <section data-oid="w:ijqa-">
            <h3
              style={{
                margin: "0 0 0.75rem 0",
                fontSize: "0.8rem",
                fontWeight: 600,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: secondaryTextColor,
              }}
              data-oid="bcq:a-_"
            >
              {t(activeLanguage, "settings.appearance")}
            </h3>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "1rem",
                borderRadius: "1rem",
                background: sectionBackground,
                border: `1px solid ${borderColor}`,
              }}
              data-oid="01200l_"
            >
              <div data-oid="8--jz.i">
                <div
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: 500,
                    color: primaryTextColor,
                    marginBottom: "0.25rem",
                  }}
                  data-oid="kblq76o"
                >
                  {t(activeLanguage, "settings.theme")}
                </div>
                <div
                  style={{ fontSize: "0.7rem", color: secondaryTextColor }}
                  data-oid="fo5-1:x"
                >
                  {settings.theme === "dark"
                    ? t(activeLanguage, "settings.darkMode")
                    : t(activeLanguage, "settings.lightMode")}
                </div>
              </div>
              <button
                onClick={handleThemeToggle}
                style={{
                  position: "relative",
                  width: "3.5rem",
                  height: "2rem",
                  borderRadius: "9999px",
                  background:
                    settings.theme === "dark"
                      ? accentColor
                      : isLightMode
                        ? "rgba(15, 23, 42, 0.22)"
                        : "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  cursor: "pointer",
                  transition: "all 0.3s",
                }}
                data-oid="zv7.wz4"
              >
                <div
                  style={{
                    position: "absolute",
                    top: "0.25rem",
                    left:
                      settings.theme === "dark"
                        ? "calc(100% - 1.75rem)"
                        : "0.25rem",
                    width: "1.5rem",
                    height: "1.5rem",
                    borderRadius: "9999px",
                    background: "white",
                    transition: "left 0.3s",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  }}
                  data-oid="43357e7"
                />
              </button>
            </div>
          </section>

          {/* Language Section */}
          <section data-oid="xw6bxoo">
            <h3
              style={{
                margin: "0 0 0.75rem 0",
                fontSize: "0.8rem",
                fontWeight: 600,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: secondaryTextColor,
              }}
              data-oid="2499gep"
            >
              {t(activeLanguage, "settings.language")}
            </h3>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
                padding: "1rem",
                borderRadius: "1rem",
                background: sectionBackground,
                border: `1px solid ${borderColor}`,
              }}
              data-oid="ui27k8b"
            >
              <label
                htmlFor="language-select"
                style={{
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  color: primaryTextColor,
                }}
                data-oid="zf58g79"
              >
                {t(activeLanguage, "settings.selectLanguage")}
              </label>
              <div style={{ position: "relative" }} data-oid="8fqex:k">
                <select
                  id="language-select"
                  value={settings.language}
                  onChange={handleLanguageChange}
                  style={{
                    width: "100%",
                    padding: "0.65rem 2.75rem 0.65rem 0.85rem",
                    borderRadius: "0.75rem",
                    background: selectBackground,
                    border: `1px solid ${borderColor}`,
                    color: primaryTextColor,
                    fontSize: "0.8rem",
                    cursor: "pointer",
                    outline: "none",
                    fontFamily,
                    appearance: "none",
                    WebkitAppearance: "none",
                    MozAppearance: "none",
                    transition: "all 0.3s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = sectionHoverBackground;
                    e.currentTarget.style.borderColor = hoverBorderColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = selectBackground;
                    e.currentTarget.style.borderColor = borderColor;
                  }}
                  data-oid="3agw5.x"
                >
                  {LANGUAGE_OPTIONS.map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                      style={{
                        background: selectOptionBackground,
                        color: selectOptionText,
                        padding: "0.5rem",
                      }}
                      data-oid="d4938zy"
                    >
                      {getLanguageOptionLabel(option.value)}
                    </option>
                  ))}
                </select>
                <span
                  className="material-symbols-outlined"
                  style={{
                    position: "absolute",
                    right: "0.85rem",
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: "1.1rem",
                    color: secondaryTextColor,
                    pointerEvents: "none",
                    fontWeight: 300,
                  }}
                  data-oid="dr8dm.o"
                >
                  expand_more
                </span>
              </div>
            </div>
          </section>

          {/* Accessibility Section */}
          <section data-oid="49bsxq0">
            <h3
              style={{
                margin: "0 0 0.75rem 0",
                fontSize: "0.8rem",
                fontWeight: 600,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: secondaryTextColor,
              }}
              data-oid="fj:6x7b"
            >
              {t(activeLanguage, "settings.accessibility")}
            </h3>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
                padding: "1rem",
                borderRadius: "1rem",
                background: sectionBackground,
                border: `1px solid ${borderColor}`,
              }}
              data-oid="98a7ik7"
            >
              <label
                htmlFor="text-size-select"
                style={{
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  color: primaryTextColor,
                }}
                data-oid=".5nxo64"
              >
                {t(activeLanguage, "settings.textAndIconSize")}
              </label>
              <div style={{ position: "relative" }} data-oid="u33:gye">
                <select
                  id="text-size-select"
                  value={settings.textSize}
                  onChange={handleTextSizeChange}
                  style={{
                    width: "100%",
                    padding: "0.65rem 2.75rem 0.65rem 0.85rem",
                    borderRadius: "0.75rem",
                    background: selectBackground,
                    border: `1px solid ${borderColor}`,
                    color: primaryTextColor,
                    fontSize: "0.8rem",
                    cursor: "pointer",
                    outline: "none",
                    fontFamily,
                    appearance: "none",
                    WebkitAppearance: "none",
                    MozAppearance: "none",
                    transition: "all 0.3s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = sectionHoverBackground;
                    e.currentTarget.style.borderColor = hoverBorderColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = selectBackground;
                    e.currentTarget.style.borderColor = borderColor;
                  }}
                  data-oid="gh4je-2"
                >
                  {TEXT_SIZE_OPTIONS.map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                      style={{
                        background: selectOptionBackground,
                        color: selectOptionText,
                        padding: "0.5rem",
                      }}
                      data-oid="-uqe8_d"
                    >
                      {option.value === "small"
                        ? t(activeLanguage, "settings.option.small")
                        : option.value === "medium"
                          ? t(activeLanguage, "settings.option.medium")
                          : t(activeLanguage, "settings.option.large")}
                    </option>
                  ))}
                </select>
                <span
                  className="material-symbols-outlined"
                  style={{
                    position: "absolute",
                    right: "0.85rem",
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: "1.1rem",
                    color: secondaryTextColor,
                    pointerEvents: "none",
                    fontWeight: 300,
                  }}
                  data-oid="38ye9q_"
                >
                  expand_more
                </span>
              </div>
            </div>
          </section>

          {/* Help Section */}
          <section data-oid="egyfkzy">
            <h3
              style={{
                margin: "0 0 0.75rem 0",
                fontSize: "0.8rem",
                fontWeight: 600,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: secondaryTextColor,
              }}
              data-oid="agh7h6e"
            >
              {t(activeLanguage, "settings.help")}
            </h3>
            <button
              onClick={() => {
                onReplayTutorial();
                onClose();
              }}
              style={{
                width: "100%",
                padding: "0.85rem 1rem",
                borderRadius: "1rem",
                background: sectionBackground,
                border: `1px solid ${borderColor}`,
                color: primaryTextColor,
                fontSize: "0.85rem",
                fontWeight: 500,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                transition: "all 0.3s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = sectionHoverBackground;
                e.currentTarget.style.borderColor = hoverBorderColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = sectionBackground;
                e.currentTarget.style.borderColor = borderColor;
              }}
              data-oid="r_q:iv_"
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "1.1rem", fontWeight: 300 }}
                data-oid=".cbm533"
              >
                replay
              </span>
              {t(activeLanguage, "settings.replayTutorial")}
            </button>
          </section>

          {/* System Section */}
          <section data-oid="khao.9n">
            <h3
              style={{
                margin: "0 0 0.75rem 0",
                fontSize: "0.8rem",
                fontWeight: 600,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: secondaryTextColor,
              }}
              data-oid="i5qxl9_"
            >
              {t(activeLanguage, "settings.system")}
            </h3>
            <button
              onClick={onResetViewer}
              style={{
                width: "100%",
                padding: "0.85rem 1rem",
                borderRadius: "1rem",
                background: "rgba(244, 63, 94, 0.1)",
                border: "1px solid rgba(244, 63, 94, 0.3)",
                color: "rgb(244, 63, 94)",
                fontSize: "0.85rem",
                fontWeight: 500,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                transition: "all 0.3s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(244, 63, 94, 0.15)";
                e.currentTarget.style.borderColor = "rgba(244, 63, 94, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(244, 63, 94, 0.1)";
                e.currentTarget.style.borderColor = "rgba(244, 63, 94, 0.3)";
              }}
              data-oid="u3cpq7p"
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "1.1rem", fontWeight: 300 }}
                data-oid="bgia6p_"
              >
                restart_alt
              </span>
              {t(activeLanguage, "settings.resetViewer")}
            </button>
          </section>
        </div>
      </div>
    </div>,
    document.body,
  );
}
